import React, { useEffect, useState, useRef } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ScrollView,
  Animated,
  Image,
  Dimensions,
  ActivityIndicator
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { db } from '../logic/firebase';
import { ref, onValue } from 'firebase/database';

const { width } = Dimensions.get("window");

const HomeScreen = () => {
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Ultime');
  const categoryAnim = useRef(new Animated.Value(0)).current;

  const categories = [
    { icon: "flash", label: "Ultime" },
    { icon: "heart", label: "Scandali" },
    { icon: "star", label: "Celebrità" },
    { icon: "camera", label: "Foto" },
  ];

  // Funzione di truncamento aggiornata che gestisce valori undefined
  const truncateText = (text, maxLines = 2) => {
    if (typeof text !== 'string') {
      return "";
    }
    const lines = text.split('\n');
    return lines.length > maxLines 
      ? lines.slice(0, maxLines).join('\n') + '...'
      : text;
  };

  useEffect(() => {
    const newsRef = ref(db, "NewsData");
    onValue(
      newsRef,
      (snapshot) => {
        const data = snapshot.val();
        const newsArray = [];
        if (data) {
          Object.keys(data).forEach((key) => {
            newsArray.push({
              id: key,
              ...data[key],
              date: new Date(data[key].createdAt).toLocaleDateString('it-IT')
            });
          });
        }
        // Ordina per data di creazione in ordine decrescente
        newsArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setNewsData(newsArray);
        setLoading(false);
      },
      (error) => {
        console.error("Errore durante il fetch dei dati da Firebase:", error);
        setLoading(false);
      }
    );
    
    // Animazione fade-in per le categorie
    Animated.timing(categoryAnim, {
      toValue: 1,
      duration: 1500,
      useNativeDriver: true,
    }).start();
  }, []);

  const filteredNews = () => {
    if (selectedCategory === 'Scandali') {
      return newsData.filter(item => 
        item.content &&
        (item.content.toLowerCase().includes('rissa') || 
         item.content.toLowerCase().includes('litigio') ||
         item.content.toLowerCase().includes('aggressione') ||
         item.content.toLowerCase().includes('scandalo') ||
         item.content.toLowerCase().includes('scontro') ||
         item.content.toLowerCase().includes('tensione'))
      );
    } else if (selectedCategory === 'Celebrità') {
      return newsData.filter(item => 
        item.content &&
        (item.content.toLowerCase().includes('vincitore') || 
         item.content.toLowerCase().includes('festival') ||
         item.content.toLowerCase().includes('sanremo') ||
         item.content.toLowerCase().includes('cantautore') ||
         item.content.toLowerCase().includes('brano') ||
         item.content.toLowerCase().includes('successo') ||
         item.content.toLowerCase().includes('evento') ||
         item.content.toLowerCase().includes('premio') ||
         item.content.toLowerCase().includes('artista') ||
         item.content.toLowerCase().includes('musica'))
      );
    } else {
      return newsData; // 'Ultime': mostra tutte le notizie
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Ultime Notizie</Text>
      </View>

      {/* Categorie */}
      <Animated.View style={[styles.categoriesContainer, { opacity: categoryAnim }]}>
        {categories.map((category, index) => (
          <TouchableOpacity 
            key={index}
            style={styles.categoryItem}
            onPress={() => setSelectedCategory(category.label)}
          >
            <Ionicons 
              name={category.icon} 
              size={28} 
              color={selectedCategory === category.label ? "#BB86FC" : "#666666"} 
            />
            <Text style={[
              styles.categoryLabel,
              { color: selectedCategory === category.label ? "#BB86FC" : "#666666" }
            ]}>
              {category.label}
            </Text>
          </TouchableOpacity>
        ))}
      </Animated.View>

      {/* Lista Notizie */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#BB86FC" />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.newsContainer} showsVerticalScrollIndicator={false}>
          {filteredNews().map((item) => (
            <View key={item.id} style={styles.newsCard}>
              <Image
                source={{ uri: item.media && item.media.url ? item.media.url : '' }}
                style={styles.newsImage}
              />
              <View style={styles.textContainer}>
                <Text style={styles.newsTitle}>{item.title}</Text>
                <Text 
                  style={styles.newsContent}
                  numberOfLines={2}
                  ellipsizeMode="tail"
                >
                  {truncateText(item.content)}
                </Text>
                <Text style={styles.newsDate}>{item.date}</Text>
              </View>
            </View>
          ))}
        </ScrollView>
      )}

      {/* Trending Section */}
      <View style={styles.trendingSection}>
        <Text style={styles.trendingTitle}>Trending Now</Text>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.trendingContainer}
        >
          {newsData.map((item) => (
            <View key={item.id} style={styles.trendingCard}>
              <Image
                source={{ uri: item.media && item.media.url ? item.media.url : '' }}
                style={styles.trendingImage}
              />
              <Text style={styles.trendingText} numberOfLines={2}>
                {item.title}
              </Text>
            </View>
          ))}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#121212',
  },
  header: {
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#252525',
  },
  headerTitle: {
    fontSize: 24,
    fontFamily: 'CustomFont',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  categoriesContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#252525',
  },
  categoryItem: {
    alignItems: 'center',
  },
  categoryLabel: {
    marginTop: 5,
    fontSize: 12,
    fontFamily: 'CustomFont',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  newsContainer: {
    paddingHorizontal: 15,
    paddingTop: 20,
  },
  newsCard: {
    flexDirection: 'row',
    backgroundColor: '#1E1E1E',
    borderRadius: 12,
    marginBottom: 15,
    padding: 10,
  },
  newsImage: {
    width: 100,
    height: 100,
    borderRadius: 8,
    marginRight: 12,
  },
  textContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  newsTitle: {
    fontSize: 16,
    fontFamily: 'CustomFont',
    color: '#FFFFFF',
    marginBottom: 5,
  },
  newsContent: {
    fontSize: 14,
    fontFamily: 'CustomFont',
    color: '#AAAAAA',
    lineHeight: 20,
    marginBottom: 5,
  },
  newsDate: {
    fontSize: 12,
    fontFamily: 'CustomFont',
    color: '#666666',
  },
  trendingSection: {
    marginTop: 20,
    paddingHorizontal: 15,
  },
  trendingTitle: {
    fontSize: 20,
    fontFamily: 'CustomFont',
    color: '#FFFFFF',
    marginBottom: 15,
  },
  trendingContainer: {
    paddingBottom: 10,
  },
  trendingCard: {
    width: 160,
    marginRight: 12,
    backgroundColor: '#1E1E1E',
    borderRadius: 10,
    padding: 10,
  },
  trendingImage: {
    width: 130,
    height: 80,
    borderRadius: 8,
    marginBottom: 8,
  },
  trendingText: {
    fontSize: 12,
    fontFamily: 'CustomFont',
    color: '#FFFFFF',
    textAlign: 'center',
    paddingHorizontal: 5,
  },
});

export default HomeScreen;
